/**
 * @file user-custom-report.ts
 * @description Nordic nRF54L 鼠标 0x07 用户参数上报解析（设备层）。
 *
 * 网吧包在 Mode 前多一个 Cmd；剥掉 Cmd（以及可选的 Report ID）后，
 * 字段布局与零售相同。由 manifest `input-report` capability 选择解析器。
 */

import type { mouseUserCustom } from '../../../types/config'

/** 用户参数包 Report ID。与 `VTNrf54LReportType.UserData` 相同。 */
const USER_REPORT_ID = 0x07

/** 网吧用户参数包 Cmd。只有 0xb0 携带可解析的用户参数。 */
export const NRF54L_CYBERCAFE_USER_REPORT_CMD = {
  userParams: 0xb0,
  pairFactoryDefault: 0xb1,
  firstConnectAfterDisconnect: 0xb2,
  outOfRange: 0xb3,
} as const

export type Nrf54LCybercafeUserReportCmd =
  (typeof NRF54L_CYBERCAFE_USER_REPORT_CMD)[keyof typeof NRF54L_CYBERCAFE_USER_REPORT_CMD]

/** 与 `InputReportCapability.config.type` 对齐。 */
export const NRF54L_MOUSE_INPUT_REPORT_TYPE = {
  Cybercafe: 'cybercafe',
} as const

/**
 * 按 input-report capability 选择解析器。
 * 网吧非 0xb0 包返回 null，调用方不得把它们当成用户参数。
 */
export function parseNrf54LMouseUserCustomData(
  buffer: Uint8Array,
  inputReportType?: string,
): mouseUserCustom | null {
  if (inputReportType === NRF54L_MOUSE_INPUT_REPORT_TYPE.Cybercafe) {
    return parseCybercafeMouseUserCustomData(buffer)
  }
  return parseRetailMouseUserCustomData(buffer)
}

function readUint16LE(buffer: Uint8Array, index: number): number {
  return (buffer[index] ?? 0) | ((buffer[index + 1] ?? 0) << 8)
}

/**
 * 零售鼠标 0x07 payload：从 Mode 开始，不含 Report ID、不含 Cmd。
 * CFG_SEL 1 字节，MS_USB_PID uint16。
 */
export function parseRetailMouseUserCustomData(buffer: Uint8Array): mouseUserCustom {
  const modeByte = buffer[0] ?? 0
  const result: mouseUserCustom = {
    conProtocol: (modeByte & 0x0f) as 0 | 1 | 2,
    sensor: ((modeByte >> 4) & 0x0f) as 0 | 1 | 2 | 3,
    cpiLev: (buffer[1] ?? 0) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    xCpiVal: readUint16LE(buffer, 2),
    yCpiVal: readUint16LE(buffer, 4),
    batStatus: (buffer[6] ?? 0) as 0 | 1 | 2,
    batPercent: buffer[7] ?? 0,
  }

  if (buffer.length > 8) {
    result.blMode = buffer[8] as 0 | 1 | 2 | 3 | 4 | 5
  }
  if (buffer.length > 9) {
    result.kRate = buffer[9] as 0 | 1 | 2 | 3 | 4 | 5 | 6
  }
  if (buffer.length > 10) {
    result.rpt = buffer[10] as 0x01 | 0x02 | 0x04 | 0x08 | 0x81 | 0x82 | 0x84
  }
  if (buffer.length > 11) {
    result.cfgSel = buffer[11]
  }
  if (buffer.length > 13) {
    result.usbPid = readUint16LE(buffer, 12)
  }
  if (buffer.length > 14) {
    const keyWheelByte = buffer[14] ?? 0
    result.keyType = (keyWheelByte & 0x0f) as 0 | 1
    result.wheelType = ((keyWheelByte >> 4) & 0x0f) as 0 | 1
  }
  if (buffer.length > 15) {
    result.rfType = buffer[15] as 0 | 1 | 2 | 3
  }
  if (buffer.length > 16) {
    result.sensorMode = buffer[16] as 0 | 1 | 2 | 3 | 4 | 5
  }
  if (buffer.length > 17) {
    const rfConnByte = buffer[17] ?? 0
    result.rfConnected = ((rfConnByte >> 7) & 0x01) as 0 | 1
    result.rfRssi = rfConnByte & 0x7f
  }

  return result
}

/**
 * 网吧鼠标用户参数包。
 *
 * USB 整包：`0x07 | Cmd | Mode...`。HID 通常已剥掉 Report ID；底座再剥掉 Cmd。
 * 剥头后的 body 与零售相同（CFG_SEL 1 字节 + PID uint16）。
 * Cmd 为 0xb1/0xb2/0xb3 时不解析用户参数。
 */
export function parseCybercafeMouseUserCustomData(buffer: Uint8Array): mouseUserCustom | null {
  let offset = 0
  if (buffer.length === 0) return null

  if (buffer[0] === USER_REPORT_ID) {
    offset = 1
  }
  if (buffer.length <= offset) return null

  const cmd = buffer[offset]
  if (
    cmd === NRF54L_CYBERCAFE_USER_REPORT_CMD.pairFactoryDefault ||
    cmd === NRF54L_CYBERCAFE_USER_REPORT_CMD.firstConnectAfterDisconnect ||
    cmd === NRF54L_CYBERCAFE_USER_REPORT_CMD.outOfRange
  ) {
    return null
  }

  if (cmd === NRF54L_CYBERCAFE_USER_REPORT_CMD.userParams) {
    offset += 1
  }

  if (buffer.length <= offset) return null
  return parseRetailMouseUserCustomData(buffer.subarray(offset))
}
